/* eslint-disable @typescript-eslint/no-var-requires */
import nock from 'nock';
import {ApiMockup} from 'common/mockup/ApiMockup';
import {HubriseOrderContract} from 'common/api/hubrise/model/order/HubriseOrderContract';
import {cloneDeep} from 'lodash';
import {HubriseResourceType} from 'common/api/hubrise/model/callback/HubriseResourceType';
import {HubriseEventType} from 'common/api/hubrise/model/callback/HubriseEventType';

export class HubriseApiMockup extends ApiMockup {
    public static readonly BASE_URL: string = 'http://api.hubrise.com';

    public static getUser(times: number = 1): void {
        const mock: nock.Scope = nock(HubriseApiMockup.BASE_URL, {allowUnmocked: false});
        mock.filteringPath((path: string) => {
            if (path.includes('/user')) {
                return '/user';
            }

            return path;
        }).get('/user').times(times).reply(200, cloneDeep(require('../../data/mockup/api/hubrise/user.json')));
    }

    public static getAccount(times: number = 1): void {
        const mock: nock.Scope = nock(HubriseApiMockup.BASE_URL, {allowUnmocked: false});
        mock.filteringPath((path: string) => {
            if (path.includes('/account')) {
                return '/account';
            }

            return path;
        }).get('/account').times(times).reply(200, cloneDeep(require('../../data/mockup/api/hubrise/account.json')));
    }

    public static getLocations(times: number = 1): void {
        const mock: nock.Scope = nock(HubriseApiMockup.BASE_URL, {allowUnmocked: false});
        mock.filteringPath((path: string) => {
            if (path.includes('/locations')) {
                return '/locations';
            }

            return path;
        }).get('/locations').times(times).reply(200, cloneDeep(require('../../data/mockup/api/hubrise/locations.json')));
    }

    public static getLocation(times: number = 1): void {
        const mock: nock.Scope = nock(HubriseApiMockup.BASE_URL, {allowUnmocked: false});
        mock.filteringPath((path: string) => {
            if (path.endsWith('/location') || path.includes('/locations/')) {
                return '/location';
            }

            return path;
        }).get('/location').times(times).reply(200, cloneDeep(require('../../data/mockup/api/hubrise/location.json')));
    }

    public static createCustomer(times: number = 1): void {
        const mock: nock.Scope = nock(HubriseApiMockup.BASE_URL, {allowUnmocked: false});
        mock.filteringPath((path: string) => {
            if (path.includes('/customer')) {
                return '/customer';
            }

            return path;
        }).post('/customer').times(times).reply(200, cloneDeep(require('../../data/mockup/api/hubrise/customer.json')));
    }

    public static updateCustomer(times: number = 1): void {
        const mock: nock.Scope = nock(HubriseApiMockup.BASE_URL, {allowUnmocked: false});
        mock.filteringPath((path: string) => {
            if (path.includes('/customer')) {
                return '/customer';
            }

            return path;
        }).put('/customer').times(times).reply(200, cloneDeep(require('../../data/mockup/api/hubrise/customer.json')));
    }

    public static getCustomer(times: number = 1): void {
        const mock: nock.Scope = nock(HubriseApiMockup.BASE_URL, {allowUnmocked: false});
        mock.filteringPath((path: string) => {
            if (path.includes('/customer_lists') && path.includes('/customers')) {
                return '/customer';
            }

            return path;
        }).get('/customer').times(times).reply(200, cloneDeep(require('../../data/mockup/api/hubrise/customer.json')));
    }

    public static getCustomers(times: number = 1): void {
        const mock: nock.Scope = nock(HubriseApiMockup.BASE_URL, {allowUnmocked: false});
        mock.filteringPath((path: string) => {
            if (path.includes('/customers')) {
                return '/customers';
            }

            return path;
        }).get('/customers').times(times).reply(200, cloneDeep(require('../../data/mockup/api/hubrise/customers.json')));
    }

    public static getCustomerLists(times: number = 1): void {
        const mock: nock.Scope = nock(HubriseApiMockup.BASE_URL, {allowUnmocked: false});
        mock.filteringPath((path: string) => {
            if (path.includes('/customer_lists')) {
                return '/customerLists';
            }

            return path;
        }).get('/customerLists').times(times).reply(200, cloneDeep(require('../../data/mockup/api/hubrise/customer_lists.json')));
    }

    public static createOrder(times: number = 1): void {
        const mock: nock.Scope = nock(HubriseApiMockup.BASE_URL, {allowUnmocked: false});
        mock.filteringPath((path: string) => {
            if (path.includes('/order')) {
                return '/order';
            }

            return path;
        }).post('/order').times(times).reply(200, cloneDeep(require('../../data/mockup/api/hubrise/order.json')));
    }

    public static getOrder(times: number = 1, generateRandomId: boolean = false): void {
        const order: HubriseOrderContract = cloneDeep(require('../../data/mockup/api/hubrise/order.json'));
        if (generateRandomId) {
            order.id = String(Date.now());
        }
        const mock: nock.Scope = nock(HubriseApiMockup.BASE_URL, {allowUnmocked: false});
        mock.filteringPath((path: string) => {
            if (path.includes('/orders/')) {
                return '/order';
            }

            return path;
        }).get('/order').times(times).reply(200, order);
    }

    public static getOrders(times: number = 1, generateRandomId: boolean = false): void {
        const orders: HubriseOrderContract[] = cloneDeep(require('../../data/mockup/api/hubrise/orders.json'));
        if (generateRandomId) {
            orders.forEach(order => order.id = String(Date.now()));
        }
        const mock: nock.Scope = nock(HubriseApiMockup.BASE_URL, {allowUnmocked: false});
        mock.filteringPath((path: string) => {
            if (path.includes('/orders') && !path.includes('/orders/')) {
                return '/orders';
            }

            return path;
        }).get('/orders').times(times).reply(200, orders);
    }

    public static setOrderStatus(times: number = 1): void {
        const mock: nock.Scope = nock(HubriseApiMockup.BASE_URL, {allowUnmocked: false});
        mock.filteringPath((path: string) => {
            if (path.includes('/orders/')) {
                return '/status';
            }

            return path;
        }).patch('/status').times(times).reply(204);
    }

    public static getCatalog(times: number = 1): void {
        const mock: nock.Scope = nock(HubriseApiMockup.BASE_URL, {allowUnmocked: false});
        mock.filteringPath((path: string) => {
            if (path.includes('/catalog')) {
                return '/catalog';
            }

            return path;
        }).get('/catalog').times(times).reply(200, cloneDeep(require('../../data/mockup/api/hubrise/catalog.json')));
    }

    public static getCatalogs(times: number = 1): void {
        const mock: nock.Scope = nock(HubriseApiMockup.BASE_URL, {allowUnmocked: false});
        mock.filteringPath((path: string) => {
            if (path.includes('/catalogs')) {
                return '/catalogs';
            }

            return path;
        }).get('/catalogs').times(times).reply(200, cloneDeep(require('../../data/mockup/api/hubrise/catalogs.json')));
    }

    public static updateWebhooks(times: number = 1): void {
        const mock: nock.Scope = nock(HubriseApiMockup.BASE_URL, {allowUnmocked: false});
        mock.filteringPath((path: string) => {
            if (path.includes('/callback')) {
                return '/webhook';
            }

            return path;
        }).post('/webhook').times(times).reply(200);
    }

    public static getWebhooks(times: number = 1): void {
        const mock: nock.Scope = nock(HubriseApiMockup.BASE_URL, {allowUnmocked: false});
        mock.filteringPath((path: string) => {
            if (path.includes('/callback')) {
                return '/webhook';
            }

            return path;
        }).get('/webhook').times(times).reply(200, {url: 'http://localhost/webhook', events: {[HubriseResourceType.ORDER]: HubriseEventType.CREATE}});
    }

    public static deleteWebhooks(times: number = 1): void {
        const mock: nock.Scope = nock(HubriseApiMockup.BASE_URL, {allowUnmocked: false});
        mock.filteringPath((path: string) => {
            if (path.includes('/callback')) {
                return '/webhook';
            }

            return path;
        }).delete('/webhook').times(times).reply(200);
    }
}
